/**
 * Spreads parallel edges that share the same source handle so smooth-step paths don't overlap.
 * Used only for rendering — do not persist `pathOffset` in scene `flowGraph`.
 */

export interface EdgeLike {
	id: string;
	source: string;
	sourceHandle?: string | null;
	data?: Record<string, unknown> | null;
}

const BASE_OFFSET = 18;
const OFFSET_STEP = 14;

function groupKey(source: string, sourceHandle: string | null | undefined): string {
	return `${source}\0${sourceHandle ?? ''}`;
}

/** Clone edges with `data.pathOffset` set for `getSmoothStepPath({ offset })`. */
export function assignParallelEdgePathOffsetsForRender<T extends EdgeLike>(edges: T[]): T[] {
	if (edges.length === 0) return edges;

	const byGroup = new Map<string, T[]>();
	for (const e of edges) {
		const k = groupKey(e.source, e.sourceHandle);
		const g = byGroup.get(k);
		if (g) g.push(e);
		else byGroup.set(k, [e]);
	}

	const offsetById = new Map<string, number>();
	for (const [, group] of byGroup) {
		if (group.length === 1) {
			offsetById.set(String(group[0].id), BASE_OFFSET);
			continue;
		}
		const sorted = [...group].sort((a, b) => String(a.id).localeCompare(String(b.id)));
		const center = (sorted.length - 1) / 2;
		sorted.forEach((e, i) => {
			const delta = (i - center) * OFFSET_STEP;
			offsetById.set(String(e.id), BASE_OFFSET + delta);
		});
	}

	return edges.map((e) => {
		const pathOffset = offsetById.get(String(e.id)) ?? BASE_OFFSET;
		const prevData = e.data && typeof e.data === 'object' ? e.data : {};
		return {
			...e,
			data: { ...prevData, pathOffset },
		};
	});
}
